import React, { useEffect, useMemo } from 'react';
import { Card, Form, Button } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, FormProvider, Controller, useFormContext } from 'react-hook-form';
import { useAppDispatch } from '../../../hooks';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import { Form as GrassrootzForm } from '../../../components';
import { entityTypes, uploadTypes } from '../../../constants/imageUpload';
import {
  IEventOrganizer,
  EventOrganizerPriority,
  eventOrganizerPriorities,
} from '../../../models/EventOrganizer';
import {
  clearEventOrganizerErrors,
  createEventOrganizer,
  updateEventOrganizer,
} from '../store/eventOrganizerActions';
import { systemSettingsRoutes } from '../../systemSettings/constants';

interface Props {
  mode: 'create' | 'edit';
  initialValue?: IEventOrganizer | null;
}

interface FormValues {
  name: string;
  mainImagePath: string | null;
  priority: EventOrganizerPriority;
}

const defaultValues = (initial?: IEventOrganizer | null): FormValues => ({
  name: initial?.name ?? '',
  mainImagePath: initial?.mainImagePath ?? null,
  priority: initial?.priority ?? 'low',
});

const EventOrganizerInput: React.FC<Props> = ({ mode, initialValue }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useSelector((s: RootState) => (s as any).eventOrganizer);

  const initialRecord = useMemo(() => {
    const maybe = initialValue as any;
    return maybe && maybe.data ? (maybe.data as IEventOrganizer) : (maybe as IEventOrganizer | null) ?? null;
  }, [initialValue]);

  const methods = useForm<FormValues>({
    defaultValues: defaultValues(initialRecord),
    mode: 'onBlur',
  });

  // Reset form when an edit record arrives asynchronously. `methods` is stable
  // across renders so we intentionally only depend on initialRecord.
  useEffect(() => {
    if (initialRecord) {
      methods.reset(defaultValues(initialRecord));
    }
  }, [initialRecord, methods]);

  const onSubmit = methods.handleSubmit((values) => {
    const payload: IEventOrganizer = {
      name: values.name.trim(),
      mainImagePath: values.mainImagePath || null,
      priority: values.priority,
    };
    if (mode === 'create') {
      dispatch(createEventOrganizer(payload) as any);
    } else if (mode === 'edit' && initialRecord?.id) {
      dispatch(updateEventOrganizer(initialRecord.id, payload) as any);
    }
  });

  // Navigate back after a successful save — but only after the user actually
  // submitted. The GET-success handler also sets `success.show = true` on
  // initial edit hydration, which would otherwise bounce us straight back to
  // the list before the form renders.
  const recordSlice = mode === 'create' ? state?.recordCreate : state?.recordEdit;
  const submitted = methods.formState.isSubmitted;
  useEffect(() => {
    if (submitted && recordSlice?.ready === 0 && recordSlice?.success?.show) {
      navigate(systemSettingsRoutes.EVENT_ORGANIZERS);
    }
  }, [recordSlice, navigate, submitted]);

  // Suppress success banner on initial GET so it only shows after submit.
  const recordToShow = useMemo(() => {
    if (submitted) return recordSlice;
    if (recordSlice?.success?.show) {
      return { ...recordSlice, success: { ...(recordSlice.success || {}), show: false } };
    }
    return recordSlice;
  }, [recordSlice, submitted]);

  return (
    <FormProvider {...methods}>
      <RecordResultMessage
        record={recordToShow}
        onDismiss={() =>
          dispatch(
            clearEventOrganizerErrors(mode === 'create' ? 'recordCreate' : 'recordEdit') as any
          )
        }
      />
      <Card fluid style={{ backgroundColor: 'var(--card-subtle-bg)' }}>
        <Card.Content>
          <Form>
            <NameField />
            <ImageField />
            <PriorityField />
          </Form>
        </Card.Content>
      </Card>
      <Button primary onClick={onSubmit}>
        <Translate value='eventOrganizers.page.input.save' />
      </Button>
      <Button onClick={() => navigate(systemSettingsRoutes.EVENT_ORGANIZERS)}>
        <Translate value='eventOrganizers.page.input.cancel' />
      </Button>
    </FormProvider>
  );
};

const NameField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='name'
      control={control}
      rules={{
        required: true,
        // Reject whitespace-only values; the submit handler trims and an
        // all-whitespace name would otherwise pass `minLength: 1` then submit
        // as an empty string.
        validate: (v) => typeof v === 'string' && v.trim().length > 0,
      }}
      render={({ field, fieldState: { error } }) => (
        <Form.Field required error={!!error}>
          <label><Translate value='eventOrganizers.page.input.name' /></label>
          <input
            id='eventOrganizerName'
            type='text'
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        </Form.Field>
      )}
    />
  );
};

const ImageField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='mainImagePath'
      control={control}
      rules={{
        validate: (v) => typeof v === 'string' && v.trim().length > 0,
      }}
      render={({ field, fieldState: { error } }) => (
        <Form.Field required error={!!error}>
          <GrassrootzForm.ImageUploadInput
            id='eventOrganizerMainImagePath'
            label={I18n.t('eventOrganizers.page.input.mainImage')}
            url={field.value || null}
            entity={entityTypes.ORGANIZATION}
            uploadType={uploadTypes.LOGO}
            position='left'
            width={350}
            height={350}
            onChange={(url?: string) => field.onChange(url ?? null)}
          />
        </Form.Field>
      )}
    />
  );
};

const PriorityField: React.FC = () => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name='priority'
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <Form.Field required>
          <label><Translate value='eventOrganizers.page.input.priority.label' /></label>
          <Form.Select
            id='eventOrganizerPriority'
            fluid
            value={field.value}
            onChange={(_e, { value }) => field.onChange(value as EventOrganizerPriority)}
            options={eventOrganizerPriorities.map((p) => ({
              key: p,
              value: p,
              text: I18n.t(`eventOrganizers.page.input.priority.options.${p}.label`),
            }))}
          />
        </Form.Field>
      )}
    />
  );
};

export default EventOrganizerInput;
